import { useState } from 'react';
import type { RefObject, DragEvent } from 'react';

type UseEditorDnDProps = {
  editorRef: RefObject<HTMLDivElement>;
  onContentChange?: (content: string) => void;
  isPreviewMode?: boolean;
};

export const useEditorDnD = ({
  editorRef,
  onContentChange,
  isPreviewMode = false,
}: UseEditorDnDProps) => {
  const [showVariables, setShowVariables] = useState(false);

  const handleEditorDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (isPreviewMode) return;
    e.preventDefault();

    let range: Range | null = null;
    if (document.caretPositionFromPoint) {
      const pos = document.caretPositionFromPoint(e.clientX, e.clientY);
      if (pos) {
        range = document.createRange();
        range.setStart(pos.offsetNode, pos.offset);
        range.collapse(true);
      }
    } else if (document.caretRangeFromPoint) {
      range = document.caretRangeFromPoint(e.clientX, e.clientY);
    }

    if (range && editorRef.current) {
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        if (range.startContainer) {
          const newRange = document.createRange();
          newRange.setStart(range.startContainer, range.startOffset);
          newRange.collapse(true);
          selection.addRange(newRange);
        }
      }
    }
  };

  const handleEditorDrop = (e: DragEvent<HTMLDivElement>) => {
    if (isPreviewMode) return;
    e.preventDefault();

    const variable = e.dataTransfer.getData('text/plain');
    if (!variable || !editorRef.current) return;

    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      range.insertNode(document.createTextNode(variable));
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    onContentChange?.(editorRef.current.innerText);
  };

  return {
    showVariables,
    setShowVariables,
    handleEditorDragOver,
    handleEditorDrop,
  };
};
